import React, { memo, useState, useCallback, useEffect } from 'react';
import InputRange from 'react-input-range';
import Autosuggest from 'react-autosuggest';
import classNames from 'classnames';

import style from './style.module.css';

/**
 * Escapes special regex characters in a string to make it safe for use in regular expressions.
 *
 * @param {string} str - The string to escape.
 * @returns {string} The escaped string with all regex special characters preceded by a backslash.
 */
function escapeRegexCharacters(string) {
  return string.replace(/[*+?^${}()|[\]\\]/g, '\\$&');
}

const SelectRange = ({ filter, onChange, rangeData, info, className, title }) => {
  const [min, setMin] = useState(String(rangeData[0]?.value ?? ''));
  const [max, setMax] = useState(String(rangeData[rangeData.length - 1]?.value ?? ''));
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (rangeData.length === 0) {
      return;
    }

    const currentValues = {
      min: info.getIn(['values', 'min']),
      max: info.getIn(['values', 'max']),
    };
    const currentText = {
      min: info.getIn(['text', 'min']),
      max: info.getIn(['text', 'max']),
    };
    const hasRangeState =
      rangeData[currentValues.min] &&
      rangeData[currentValues.max] &&
      String(currentText.min) === String(rangeData[currentValues.min].value) &&
      String(currentText.max) === String(rangeData[currentValues.max].value);

    if (hasRangeState) {
      setMin(String(currentText.min));
      setMax(String(currentText.max));

      return;
    }

    onChange({
      filterName: filter.get('name'),
      filterData: {
        values: {
          min: 0,
          max: rangeData.length - 1,
        },
        text: {
          min: rangeData[0].value,
          max: rangeData[rangeData.length - 1].value,
        },
      },
    });
  }, []);

  useEffect(() => {
    const minText = info.getIn(['text', 'min']);
    const maxText = info.getIn(['text', 'max']);

    if (minText !== '0.00') {
      setMin(String(minText));
    }

    if (maxText !== '10.00') {
      setMax(String(maxText));
    }
  }, [info]);

  const handleChange = useCallback((_, { newValue }, value) => {
    // eslint-disable-next-line no-unused-expressions
    value === 'min' ? setMin(newValue) : setMax(newValue);
  }, []);

  const handleSelectValueChange = useCallback(
    (_, { suggestionValue }, name) => {
      const minText = info.getIn(['text', 'min']);
      const maxText = info.getIn(['text', 'max']);

      let resolvedValue = suggestionValue;

      if (name === 'min' && Number(suggestionValue) >= Number(maxText)) {
        resolvedValue = maxText;
      }

      if (name === 'max' && Number(suggestionValue) <= Number(minText)) {
        resolvedValue = minText;
      }

      resolvedValue = String(resolvedValue);

      // eslint-disable-next-line no-unused-expressions
      name === 'min' ? setMin(resolvedValue) : setMax(resolvedValue);

      const rangeValue = rangeData.findIndex(({ value }) => value === resolvedValue);

      onChange({
        filterName: filter.get('name'),
        filterData: {
          values: {
            [name]: rangeValue,
          },
          text: {
            [name]: resolvedValue,
          },
        },
      });
    },
    [info, rangeData, filter, onChange],
  );

  const handleBlur = useCallback(
    (event, _, name) => {
      const existValue = rangeData.some(({ value }) => event.target.value === value);

      if (!existValue) {
        if (name === 'min') {
          setMin(String(rangeData[0].value));
          onChange({
            filterName: filter.get('name'),
            filterData: {
              values: { [name]: 0 },
              text: { [name]: rangeData[0].value },
            },
          });
        }

        if (name === 'max') {
          setMax(String(rangeData[rangeData.length - 1].value));
          onChange({
            filterName: filter.get('name'),
            filterData: {
              values: { [name]: rangeData.length - 1 },
              text: { [name]: rangeData[rangeData.length - 1].value },
            },
          });
        }
      } else {
        handleSelectValueChange(event, { suggestionValue: event.target.value }, name);
      }
    },
    [rangeData, filter, onChange, handleSelectValueChange],
  );

  const handleRangeValuesChange = useCallback(
    values => {
      const minIdx = Math.max(0, values.min);
      const maxIdx = Math.min(rangeData.length - 1, values.max);

      const minTextValue = String(rangeData[minIdx].value);
      const maxTextValue = String(rangeData[maxIdx].value);

      setMin(minTextValue);
      setMax(maxTextValue);

      onChange({
        filterName: filter.get('name'),
        filterData: {
          values: { min: minIdx, max: maxIdx },
          text: { min: minTextValue, max: maxTextValue },
        },
      });
    },
    [rangeData, filter, onChange],
  );

  const getSuggestions = useCallback(
    value => {
      const escapedValue = escapeRegexCharacters(String(value ?? '').trim());
      const regex = new RegExp(`^${escapedValue}`, 'i');

      return rangeData.filter(({ value: v }) => regex.test(v));
    },
    [rangeData],
  );

  const handleSuggestionsFetchRequested = useCallback(
    ({ value }) => {
      setSuggestions(getSuggestions(value));
    },
    [getSuggestions],
  );

  const handleSuggestionsClearRequested = useCallback(() => {
    setSuggestions([]);
  }, []);

  const shouldRenderSuggestions = useCallback(() => true, []);

  if (rangeData.length === 0) {
    return null;
  }

  const maxValue = rangeData.length - 1;
  const rangeValues = {
    min: info.getIn(['values', 'min']),
    max: info.getIn(['values', 'max']),
  };

  const suggestionValue = data => data?.value ?? '';
  const renderSuggestion = data => <div className="react-autosuggest__suggestion-wrapper">{suggestionValue(data)}</div>;

  return (
    <div className={classNames(style.host, className)}>
      {title && <span className={classNames(style.title, 'field-label')}>{title}</span>}
      <InputRange maxValue={maxValue} minValue={0} onChange={handleRangeValuesChange} step={1} value={rangeValues} />
      <div className={style.range}>
        <div className={style.grow}>
          <Autosuggest
            getSuggestionValue={suggestionValue}
            inputProps={{
              className: 'form-control',
              value: min,
              onChange: (...args) => handleChange(...args, 'min'),
              onBlur: (...args) => handleBlur(...args, 'min'),
            }}
            onSuggestionsClearRequested={handleSuggestionsClearRequested}
            onSuggestionSelected={(...args) => handleSelectValueChange(...args, 'min')}
            onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
            renderSuggestion={renderSuggestion}
            shouldRenderSuggestions={shouldRenderSuggestions}
            suggestions={suggestions}
          />
        </div>
        <span>-</span>
        <div className={style.grow}>
          <Autosuggest
            getSuggestionValue={suggestionValue}
            inputProps={{
              className: 'form-control',
              value: max,
              onChange: (...args) => handleChange(...args, 'max'),
              onBlur: (...args) => handleBlur(...args, 'max'),
            }}
            onSuggestionsClearRequested={handleSuggestionsClearRequested}
            onSuggestionSelected={(...args) => handleSelectValueChange(...args, 'max')}
            onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
            renderSuggestion={renderSuggestion}
            shouldRenderSuggestions={shouldRenderSuggestions}
            suggestions={suggestions}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(SelectRange);
